import { useQueries, useQuery } from '@tanstack/react-query';
import { CalendarClock, ShieldCheck, Wrench } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { BarList } from '../components/charts/BarList.js';
import { StatTile } from '../components/charts/StatTile.js';
import { TrendChart, type TrendPoint } from '../components/charts/TrendChart.js';
import { Avatar, Badge } from '../components/ui/Badge.js';
import { Card, CardHeader, PageHeader } from '../components/ui/Layout.js';
import { EmptyState, ErrorBanner, SkeletonCards } from '../components/ui/States.js';
import { RelativeTime } from '../components/ui/Time.js';
import { ProviderBoard } from '../features/dashboard/ProviderBoard.js';
import { buildProviderStatuses } from '../features/dashboard/providers.js';
import {
  alarmsApi,
  integrationsApi,
  maintenanceApi,
  reportsApi,
  schedulesApi,
} from '../lib/api.js';
import { densePerDay, lastTwoDays, meanSeconds } from '../features/reports/shape.js';
import { compactNumber, formatDuration, percentDelta } from '../lib/format.js';
import { onCallAt, resolveOnCall, type ResolvedSpan } from '../lib/oncall.js';
import type { OnCallResponse, Schedule } from '../lib/schemas.js';

const DAY_MS = 86_400_000;

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * DAY_MS).toISOString();
}

/**
 * The executive landing page: KPI tiles, a 30-day trend, the provider status
 * board, who is on call now and the maintenance windows in force.
 */
export function DashboardPage() {
  const summary = useQuery({
    queryKey: ['reports', 'summary', '30d'],
    queryFn: () => reportsApi.summary({ from: isoDaysAgo(30) }),
    retry: false,
  });
  const mtta = useQuery({
    queryKey: ['reports', 'mtta', '30d'],
    queryFn: () => reportsApi.mtta({ from: isoDaysAgo(30) }),
    retry: false,
  });
  const mttr = useQuery({
    queryKey: ['reports', 'mttr', '30d'],
    queryFn: () => reportsApi.mttr({ from: isoDaysAgo(30) }),
    retry: false,
  });
  const noisiest = useQuery({
    queryKey: ['reports', 'noisiest', '30d', 'integration'],
    queryFn: () => reportsApi.noisiest({ from: isoDaysAgo(30), by: 'integration', limit: 6 }),
    retry: false,
  });
  const integrations = useQuery({
    queryKey: ['integrations', 'all'],
    queryFn: () => integrationsApi.listAll(),
    retry: false,
  });
  const openAlarms = useQuery({
    queryKey: ['alarms', 'dashboard-open'],
    queryFn: () => alarmsApi.list({ state: 'active', limit: 200 }),
    retry: false,
  });
  const windows = useQuery({
    queryKey: ['maintenance', 'active'],
    queryFn: () => maintenanceApi.active(),
    retry: false,
  });
  const schedules = useQuery({
    queryKey: ['schedules', 'all'],
    queryFn: () => schedulesApi.listAll(),
    retry: false,
  });

  const scheduleList = schedules.data ?? [];
  const onCallQueries = useQueries({
    queries: scheduleList.map((schedule) => ({
      queryKey: ['schedules', schedule.id, 'on-call', 'now'],
      queryFn: () => schedulesApi.onCall(schedule.id, { at: new Date().toISOString() }),
      retry: false,
    })),
  });

  const alarms = openAlarms.data?.items ?? [];

  const openCritical = alarms.filter(
    (alarm) => alarm.severity === 'critical' && alarm.state !== 'closed',
  ).length;
  const openMajor = alarms.filter(
    (alarm) => alarm.severity === 'major' && alarm.state !== 'closed',
  ).length;

  const perDay = useMemo(() => densePerDay(summary.data), [summary.data]);
  const { latest: last24, previous: previous24 } = lastTwoDays(perDay);

  const trend: TrendPoint[] = perDay.map((row) => ({ date: row.date, values: [row.count] }));
  const sparkline = perDay.slice(-12).map((row) => row.count);

  const openByIntegration = useMemo(() => {
    const map = new Map<string, number>();
    for (const alarm of alarms) {
      if (alarm.state === 'closed') continue;
      map.set(alarm.integration_id, (map.get(alarm.integration_id) ?? 0) + 1);
    }
    return map;
  }, [alarms]);

  const providerStatuses = useMemo(
    () => buildProviderStatuses(integrations.data ?? [], openByIntegration),
    [integrations.data, openByIntegration],
  );

  const noisiestRows = (noisiest.data?.items ?? []).map((row) => ({
    label: row.label ?? row.key ?? 'Not set',
    sublabel: `${compactNumber(row.occurrences)} occurrences`,
    value: row.alarms,
  }));

  const onCallRows = scheduleList.map((schedule, index) => ({
    schedule,
    spans: resolveOnCallNow(schedule, onCallQueries[index]?.data),
  }));

  const reportsUnavailable = summary.error ?? mtta.error ?? mttr.error;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Operations overview"
        description="The last 30 days across every connected source."
        actions={
          <Link
            to="/reports"
            className="text-[var(--text-base)] text-[var(--accent-ink)] underline underline-offset-2"
          >
            Full reports
          </Link>
        }
      />

      {reportsUnavailable && (
        <ErrorBanner
          title="Reporting data is unavailable"
          error={reportsUnavailable}
          onRetry={() => {
            void summary.refetch();
            void mtta.refetch();
            void mttr.refetch();
          }}
        />
      )}

      <section aria-label="Key figures">
        {openAlarms.isPending || summary.isPending ? (
          <SkeletonCards count={5} className="sm:grid-cols-2 lg:grid-cols-5" />
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile
              label="Open critical"
              value={compactNumber(openCritical)}
              tone={openCritical > 0 ? 'var(--sev-critical)' : undefined}
              footnote="Unresolved, severity critical"
            />
            <StatTile
              label="Open major"
              value={compactNumber(openMajor)}
              tone={openMajor > 0 ? 'var(--sev-major)' : undefined}
              footnote="Unresolved, severity major"
            />
            <StatTile
              label="Alarms, last 24 h"
              value={compactNumber(last24)}
              delta={percentDelta(last24, previous24)}
              deltaPeriod="the previous 24 hours"
              trend={sparkline}
            />
            <StatTile
              label="Mean time to acknowledge"
              value={formatDuration(meanSeconds(mtta.data))}
              footnote={`${compactNumber(mtta.data?.overall.count ?? 0)} alarms measured`}
            />
            <StatTile
              label="Mean time to resolve"
              value={formatDuration(meanSeconds(mttr.data))}
              footnote={`${compactNumber(mttr.data?.overall.count ?? 0)} alarms measured`}
            />
          </div>
        )}
      </section>

      {/* Cards size to their content rather than to the tallest neighbour, so
          the chart is not stranded above a field of empty surface. */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
        <Card>
          <CardHeader
            title="Alarms per day"
            description="Every alarm created in the last 30 days."
          />
          <TrendChart
            points={trend}
            series={[{ label: 'Alarms', color: 'var(--chart-1)' }]}
            caption="Alarms created per day over the last 30 days"
            height={280}
          />
        </Card>

        <Card>
          <CardHeader title="Noisiest sources" description="By alarm count, last 30 days." />
          <BarList rows={noisiestRows} valueSuffix="alarms" />
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Provider status"
          description="Health of the SaaS sources VAM polls, plus the incidents they have raised."
          actions={
            <Link
              to="/integrations"
              className="text-[var(--text-base)] text-[var(--accent-ink)] underline underline-offset-2"
            >
              Manage
            </Link>
          }
        />
        {integrations.isPending ? (
          <SkeletonCards count={6} className="sm:grid-cols-2 lg:grid-cols-3" />
        ) : integrations.error ? (
          <ErrorBanner error={integrations.error} onRetry={() => void integrations.refetch()} />
        ) : (
          <ProviderBoard statuses={providerStatuses} />
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="On call now"
            description="Resolved from each team's schedule, overrides included."
            actions={<CalendarClock className="h-4 w-4 text-[var(--text-3)]" aria-hidden />}
          />
          {schedules.isPending ? (
            <SkeletonCards count={2} />
          ) : schedules.error ? (
            <ErrorBanner error={schedules.error} onRetry={() => void schedules.refetch()} />
          ) : onCallRows.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck className="h-6 w-6" />}
              title="No schedules yet"
              description="Create a schedule so escalations know who to wake."
              action={
                <Link
                  to="/schedules"
                  className="text-[var(--text-base)] text-[var(--accent-ink)] underline underline-offset-2"
                >
                  Create a schedule
                </Link>
              }
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {onCallRows.map(({ schedule, spans }) => (
                <li
                  key={schedule.id}
                  className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--border)] px-2.5 py-2"
                >
                  <span className="min-w-0 flex-1 truncate text-[var(--text-base)] text-[var(--text)]">
                    <Link to={`/schedules/${schedule.id}`} className="hover:underline">
                      {schedule.name}
                    </Link>
                  </span>
                  {spans.length === 0 ? (
                    <span className="text-[var(--text-base)] text-[var(--text-3)]">
                      Nobody on call
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      {spans.slice(0, 3).map((span, i) => (
                        <span key={i} className="flex items-center gap-1">
                          <Avatar name={span.user?.display_name ?? null} size={18} />
                          <span className="text-[var(--text-base)] text-[var(--text-2)]">
                            {span.user?.display_name ?? 'Unassigned'}
                          </span>
                          {span.override_id && <Badge>override</Badge>}
                        </span>
                      ))}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Maintenance in force"
            description="Windows suppressing notifications right now."
            actions={<Wrench className="h-4 w-4 text-[var(--text-3)]" aria-hidden />}
          />
          {windows.isPending ? (
            <SkeletonCards count={2} />
          ) : windows.error ? (
            <ErrorBanner error={windows.error} onRetry={() => void windows.refetch()} />
          ) : (windows.data?.length ?? 0) === 0 ? (
            <EmptyState title="No active windows" description="Nothing is being suppressed." />
          ) : (
            <ul className="flex flex-col gap-2">
              {(windows.data ?? []).map((window) => (
                <li
                  key={window.id}
                  className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--border)] px-2.5 py-2"
                >
                  <span className="min-w-0 flex-1 truncate text-[var(--text-base)]">
                    {window.name}
                  </span>
                  <span className="text-[var(--text-base)] text-[var(--text-2)]">
                    ends <RelativeTime value={window.next_end ?? window.ends_at} withSuffix />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

/** Prefer the API's answer; fall back to resolving the schedule locally. */
function resolveOnCallNow(
  schedule: Schedule,
  response: OnCallResponse | undefined,
): ResolvedSpan[] {
  if (response && response.users.length > 0) {
    return response.users.map((user, index) => ({
      start: response.starts_at ?? response.at,
      end: response.ends_at ?? response.at,
      user: { id: user.id, display_name: user.display_name, email: user.email },
      shift_id: response.shift_id,
      shift_name: response.shift_name,
      override_id: response.source === 'override' ? response.schedule_id : null,
      participant_index: index,
    }));
  }
  if (response) return [];
  const now = new Date();
  const spans = resolveOnCall({
    schedule,
    from: new Date(now.getTime() - DAY_MS),
    to: new Date(now.getTime() + DAY_MS),
  });
  return onCallAt(spans, now);
}
